import { getLocale, type Locale } from '../locale';

import * as agents from './agents';
import * as app from './app';
import * as boot from './boot';
import * as certificate from './certificate';
import * as certificateCourse from './certificateCourse';
import * as certificatePage from './certificatePage';
import * as chat from './chat';
import * as checkpoint from './checkpoint';
import * as civil from './civil';
import * as credibility from './credibility';
import * as efficiency from './efficiency';
import * as explainer from './explainer';
import * as foundations from './foundations';
import * as home from './home';
import * as learning from './learning';
import * as iris from './iris';
import * as person from './person';
import * as play from './play';
import * as power from './power';
import * as profile from './profile';
import * as progress from './progress';
import * as reading from './reading';
import * as standing from './standing';
import * as support from './support';
import * as warmup from './warmup';
import * as verification from './verification';

/**
 * Every word the interface says, in every language.
 *
 * ## Why this is an object graph and not a `t('some.key')` lookup
 *
 * Because a missing string should not be a thing that can happen. With keys,
 * `t('home.heroo.title')` is a typo that compiles, ships, and shows up as a
 * blank heading or a raw key in front of a learner. Here `s.home.heroo.title`
 * does not compile, and neither does calling a sentence with the wrong number
 * of arguments, nor forgetting a plural form.
 *
 * The mechanism is one line per domain file:
 *
 *     export const en = { ... };
 *     export const ar: typeof en = { ... };
 *
 * `typeof en` is the whole enforcement. A key added to `en` and not to `ar` is
 * a type error in `ar`; a key renamed in one is an error in the other; a
 * sentence that takes a count in English and forgot it in Arabic is an error.
 * Four hundred strings is far past the size where a review catches that, and
 * `tsc` catches all of it for free. Note that neither object may be `as
 * const` — literal types would make `ar` have to repeat the English *words*,
 * which is the opposite of the point.
 *
 * ## Why it is split by domain
 *
 * One file per area of the product, because the alternative is one file that
 * every change to the interface has to touch. Splitting it means the chat
 * strings and the level-list strings are edited independently and never
 * collide, and it means each file is small enough to read whole.
 *
 * ## Sentences are functions
 *
 * Anything with a number, a name or a count in it is a function, never a
 * template assembled at the call site:
 *
 *     objectivesToGo: (n: number) => `${n} objectives to go…`
 *
 * This is not a style preference. Arabic has six plural categories where
 * English has two, and word order is not the same, so a sentence built by
 * concatenating "3" + " objectives to go" in a component cannot be translated
 * at all — the pieces arrive in the wrong order and in the wrong number. A
 * function lets each language build its own sentence from the same facts.
 *
 * ## Sentences with elements in them
 *
 * A few sentences have a component in the middle — a star, a piece of bold.
 * Those return an array of parts rather than a string, so that each language
 * decides where the element goes:
 *
 *     hintCost: (stars: ReactNode) => ['This one costs ', stars, ' of three.']
 *
 * Render them with `<Parts of={…} />` from web/src/ui. Keeping them as data
 * rather than JSX is what lets this whole directory stay plain TypeScript,
 * testable by `node --test` without a renderer.
 */

export interface Strings {
  app: typeof app.en;
  boot: typeof boot.en;
  certificate: typeof certificate.en;
  certificateCourse: typeof certificateCourse.en;
  certificatePage: typeof certificatePage.en;
  chat: typeof chat.en;
  checkpoint: typeof checkpoint.en;
  civil: typeof civil.en;
  credibility: typeof credibility.en;
  efficiency: typeof efficiency.en;
  explainer: typeof explainer.en;
  foundations: typeof foundations.en;
  home: typeof home.en;
  learning: typeof learning.en;
  iris: typeof iris.en;
  person: typeof person.en;
  play: typeof play.en;
  power: typeof power.en;
  profile: typeof profile.en;
  progress: typeof progress.en;
  reading: typeof reading.en;
  standing: typeof standing.en;
  support: typeof support.en;
  agents: typeof agents.en;
  warmup: typeof warmup.en;
  verification: typeof verification.en;
}

const CATALOGUE: Record<Locale, Strings> = {
  en: {
    app: app.en,
    boot: boot.en,
    certificate: certificate.en,
    certificateCourse: certificateCourse.en,
    certificatePage: certificatePage.en,
    chat: chat.en,
    checkpoint: checkpoint.en,
    civil: civil.en,
    credibility: credibility.en,
    efficiency: efficiency.en,
    explainer: explainer.en,
    foundations: foundations.en,
    home: home.en,
    learning: learning.en,
    iris: iris.en,
    person: person.en,
    play: play.en,
    power: power.en,
    profile: profile.en,
    progress: progress.en,
    reading: reading.en,
    standing: standing.en,
    support: support.en,
    agents: agents.en,
    warmup: warmup.en,
    verification: verification.en,
  },
  ar: {
    app: app.ar,
    boot: boot.ar,
    certificate: certificate.ar,
    certificateCourse: certificateCourse.ar,
    certificatePage: certificatePage.ar,
    chat: chat.ar,
    checkpoint: checkpoint.ar,
    civil: civil.ar,
    credibility: credibility.ar,
    efficiency: efficiency.ar,
    explainer: explainer.ar,
    foundations: foundations.ar,
    home: home.ar,
    learning: learning.ar,
    iris: iris.ar,
    person: person.ar,
    play: play.ar,
    power: power.ar,
    profile: profile.ar,
    progress: progress.ar,
    reading: reading.ar,
    standing: standing.ar,
    support: support.ar,
    agents: agents.ar,
    warmup: warmup.ar,
    verification: verification.ar,
  },  de: {
    app: app.de,
    boot: boot.de,
    certificate: certificate.de,
    certificateCourse: certificateCourse.de,
    certificatePage: certificatePage.de,
    chat: chat.de,
    checkpoint: checkpoint.de,
    civil: civil.de,
    credibility: credibility.de,
    efficiency: efficiency.de,
    explainer: explainer.de,
    foundations: foundations.de,
    home: home.de,
    learning: learning.de,
    iris: iris.de,
    person: person.de,
    play: play.de,
    power: power.de,
    profile: profile.de,
    progress: progress.de,
    reading: reading.de,
    standing: standing.de,
    support: support.de,
    agents: agents.de,
    warmup: warmup.de,
    verification: verification.de,
  },
  ja: {
    app: app.ja,
    boot: boot.ja,
    certificate: certificate.ja,
    certificateCourse: certificateCourse.ja,
    certificatePage: certificatePage.ja,
    chat: chat.ja,
    checkpoint: checkpoint.ja,
    civil: civil.ja,
    credibility: credibility.ja,
    efficiency: efficiency.ja,
    explainer: explainer.ja,
    foundations: foundations.ja,
    home: home.ja,
    learning: learning.ja,
    iris: iris.ja,
    person: person.ja,
    play: play.ja,
    power: power.ja,
    profile: profile.ja,
    progress: progress.ja,
    reading: reading.ja,
    standing: standing.ja,
    support: support.ja,
    agents: agents.ja,
    warmup: warmup.ja,
    verification: verification.ja,
  },
};

/** The catalogue for one language. Used by the provider, and by the tests. */
export function stringsFor(locale: Locale): Strings {
  return CATALOGUE[locale];
}

/**
 * The strings for the language the page is in right now.
 *
 * For the modules that build sentences without being components — `verdict`,
 * `lockedMessage`, the rank blurbs, the backup failure reasons. A component
 * must use `useStrings()` from state/useLocale instead, so that changing the
 * language re-renders it; this function would hand a component the right
 * words at a moment when nothing had told React to ask again.
 */
export function getStrings(): Strings {
  return CATALOGUE[getLocale()];
}
